/**
 * src/main.js — entry point del bundle.
 *
 * Orquesta el bootstrap (auth + cache de grupos) y las 3 vistas de arranque:
 * renderLanding, renderIngreso, renderCrearGrupo.
 *
 * FASE 4 · F1.3.b: grupos y jugadores viven en Supabase Postgres. Crear grupo
 * y unirse por código pasan por los RPCs createGroup/joinGroup (groups.js).
 * Las ramas `!currentUser` murieron — con el gate de auth (F1.2) siempre hay
 * sesión dentro de la app. Botones mock del landing eliminados (DT-M).
 */

import { getCurrentUser, initAuth, onAuthStateChange, signOut } from './core/auth.js';
import {
  createGroup,
  joinGroup,
  getGroupPreview,
  cargarMisGrupos,
  getActiveMembershipsForUser,
  MAX_GROUPS_PER_USER,
  setActiveGroupCode,
  getKnownGroups,
  getGroupDisplayName,
  switchActiveGroup,
  esUsuarioMiembroDelGrupo,
  gruposLoadFailed,
} from './core/groups.js';
import { navigate, router } from './core/router.js';
import { esRutaPublica } from './core/rutas-publicas.js';
import { renderLogin, renderResetPassword, renderInvitado } from './modules/login.js';
import { renderBrandLogo } from './ui/brand-logo.js';
import { scheduleBootWatchdog } from './ui/boot-watchdog.js';
import { hideLoadingOverlay } from './ui/loading-overlay.js';
import * as Sentry from '@sentry/browser';
import { initSentry } from './core/telemetry.js';

// ─── H5: Sentry — alarma de errores en producción ──────────────────────────
// Lo más temprano posible en el boot, envuelto en try/catch: si Sentry falla
// (red, config), el arranque de la app NO se cae. Sin VITE_SENTRY_DSN no se
// inicializa (local sin .env.local no manda nada). __APP_COMMIT__ lo inyecta
// vite.config.js en build (hash del commit) para taggear el release.
try {
  if (typeof window !== 'undefined') {
    initSentry(Sentry, {
      dsn: import.meta.env.VITE_SENTRY_DSN,
      hostname: window.location.hostname,
      release: typeof __APP_COMMIT__ !== 'undefined' ? __APP_COMMIT__ : undefined,
    });
    // Herramienta oficial de prueba de Sentry. Captura DIRECTA porque
    // window.onerror NO se dispara con errores tipeados en la consola de
    // DevTools. Correr `__pmSentryTest()` en la consola y verificar el issue
    // "test sentry pacto-mundial" en el panel. Ver CLAUDE.md.
    window.__pmSentryTest = () => Sentry.captureException(new Error('test sentry pacto-mundial'));
  }
} catch (err) {
  console.error('[sentry] init falló (no bloquea el boot):', err);
}

// ─── Bridge path→hash para deep links externos (fix-spa-routing-vercel) ────
//
// Cuando alguien abre pactomundial.futbol/g/CODIGO desde WhatsApp/mail, Vercel
// no encuentra archivo fisico para ese path. Con el rewrite "/(.*)" del
// vercel.json (raíz del repo), Vercel sirve index.html y el bundle se carga,
// pero el router del cliente lee window.location.hash — que llega vacio —
// y cae a Pantalla 1, perdiendo el codigo del invitado.
//
// Este bridge corre ANTES de cualquier lectura de hash (al top del modulo,
// post-imports). Si llegamos con un path real != '/' (deep link externo),
// lo promueve al hash via replaceState — el router lo procesa normal.
// Excepciones: '/' (raiz) y '/fixture-sandbox.html' (pagina separada del
// build multi-page de Vite) NO se tocan.
//
// Seguro frente al OAuth de Supabase: el redirectTo es window.location.origin
// (pathname '/'), asi que el callback PKCE nunca dispara el bridge.
//
// history.replaceState (no pushState): no rompe el boton atras del browser.
if (typeof window !== 'undefined' && typeof history !== 'undefined') {
  const p = window.location.pathname;
  if (p !== '/' && p !== '/fixture-sandbox.html') {
    const newHash = '#' + p + window.location.search;
    history.replaceState(null, '', '/' + newHash);
  }
}

// Bloque 8: clave consumida por bootstrap para auto-join post-OAuth Google
// del carril del invitado. login.js escribe { intent:'invitado', codigo, apodo }
// en sessionStorage ANTES del redirect; tras volver con sesion, leemos y
// disparamos joinGroup automaticamente.
const LOGIN_INTENT_KEY = 'pacto:loginIntent';

// Bloque 8 (pulir flash): flag de "join in progress". login.js lo setea
// antes de signUp/signIn/Google (carril invitado) y main.js lo limpia
// dentro de onJoinFromInvitado tras router(). Mientras esta activo,
// handleGuestRoute pinta el loader "Sumandote..." en vez de renderInvitado
// modo logueado — evita el flash entre signUp.SIGNED_IN y joinGroup OK.
// String duplicado en login.js (mantener sincronizado).
const JOIN_IN_PROGRESS_KEY = 'pacto:joining';

const isJoiningCode = (codigo) => {
  try { return sessionStorage.getItem(JOIN_IN_PROGRESS_KEY) === codigo; }
  catch (_) { return false; }
};
const clearJoinInProgress = () => {
  try { sessionStorage.removeItem(JOIN_IN_PROGRESS_KEY); } catch (_) { /* ignore */ }
};

// Traduce los errores crudos de los RPCs crear_grupo / unirse_a_grupo.
const traducirErrorGrupo = (error) => {
  const m = (error?.message || '').toLowerCase();
  if (m.includes('codigo_invalido')) return 'El grupo no existe.';
  if (m.includes('ya_sos_miembro')) return 'Ya sos parte de este grupo.';
  if (m.includes('limite_grupos')) return 'Llegaste al límite de grupos por usuario.';
  if (m.includes('apodo_ocupado')) return 'Ese apodo ya está usado en este grupo, elegí otro.';
  if (m.includes('grupo_cerrado')) return 'Ese grupo ya está cerrado.';
  if (m.includes('nombre_vacio')) return 'Completá el nombre del grupo.';
  if (m.includes('apodo_vacio')) return 'Completá cómo te conocen en el grupo.';
  if (m.includes('usuario_inexistente')) return 'Tu cuenta no está lista. Recargá e intentá de nuevo.';
  return 'No pudimos completar la operación. Probá de nuevo.';
};

// ─── 3. Landing — Pantalla 1 (v2.1 §2.1, Bloque 5) ─────────────────────────
//
// Pantalla 1: 4 opciones para el usuario que entra al dominio pelado
// pactomundial.futbol sin sesión. Cada botón funcional setea el intent y
// llama renderLogin(intent) (Bloque 4); post-login, applyIntent navega al
// destino correcto. "Soy extranjero" queda disabled (v2.1 §2.6).
//
// Si la pantalla termina visible CON sesión (caso edge: router rama 3 cuando
// hash='/' y el usuario logueado no tiene grupos), los botones funcionales
// navegan directo en vez de pedir login — evita un loop a renderLogin con
// sesión activa. DT-DH cubre el caso de "Mis grupos activos" en ese edge
// (botón no dispara hashchange si hash ya es '/') — se resuelve en Bloque 8.
//
// Sin botón "Cerrar sesión" (v2.1 §4.2: cerrar sesión vive en Perfil). El
// usuario edge sesión+sin-grupos no tiene escape desde aquí — DT-DI.

export const renderLanding = () => {
  document.getElementById('app-root').innerHTML = `
        <div class="view-enter" style="text-align: center; margin-top: 2rem;">
            ${renderBrandLogo()}
            <p class="subtitle" style="font-weight: 500; font-size: 1.1rem; color: var(--text-muted); letter-spacing: 0.04em; margin: 0.5rem 0 0;">
                Rituales futboleros argentinos <span data-ar-flag="1" role="img" aria-label="Argentina" style="display:inline-block; margin-left:.2em; vertical-align:-0.3em; line-height:0;"><svg viewBox="0 0 30 20" width="28" height="18.7" style="width:2.1em; height:1.4em; border-radius:3px; box-shadow:0 1px 4px rgba(0,0,0,.34), 0 0 0 .6px rgba(255,255,255,.16); transform:rotate(-4deg);"><defs><clipPath id="arFlagRounded"><rect width="30" height="20" rx="3.4"/></clipPath></defs><g clip-path="url(#arFlagRounded)"><rect width="30" height="20" fill="#fff"/><rect width="30" height="6.667" fill="#74ACDF"/><rect y="13.333" width="30" height="6.667" fill="#74ACDF"/><g transform="translate(15,10)"><polygon fill="#F6B40E" points="3.2,0 1.835,0.492 2.771,1.6 1.344,1.344 1.6,2.771 0.492,1.835 0,3.2 -0.492,1.835 -1.6,2.771 -1.344,1.344 -2.771,1.6 -1.835,0.492 -3.2,0 -1.835,-0.492 -2.771,-1.6 -1.344,-1.344 -1.6,-2.771 -0.492,-1.835 0,-3.2 0.492,-1.835 1.6,-2.771 1.344,-1.344 2.771,-1.6 1.835,-0.492"/><circle r="2" fill="#F6B40E" stroke="#E0A106" stroke-width=".45"/></g></g></svg></span>
            </p>
            <p class="subtitle" style="font-weight: 500; font-size: 0.95rem; color: rgba(255,255,255,0.72); margin: 0.35rem 0 1.8rem;">
                Con amigos, siempre con amigos
            </p>

            <button class="btn btn-primary"   id="btnP1Crear">Crear grupo</button>
            <button class="btn btn-secondary" id="btnP1Unirse">Sumarme a un grupo</button>
            <button class="btn btn-secondary" id="btnP1MisGrupos">Mis grupos activos</button>
            <button class="btn btn-secondary" id="btnP1Extranjero" disabled style="opacity:.45; cursor:not-allowed;">Soy extranjero, quiero sumarme</button>
        </div>
    `;

  const goWithIntent = (intent, hashRoute) => {
    if (getCurrentUser()) { window.location.hash = hashRoute; return; }
    renderLogin(intent);
  };
  document.getElementById('btnP1Crear')    ?.addEventListener('click', () => goWithIntent('crear',      '/crear-grupo'));
  document.getElementById('btnP1Unirse')   ?.addEventListener('click', () => goWithIntent('unirse',     '/ingreso'));
  document.getElementById('btnP1MisGrupos')?.addEventListener('click', () => goWithIntent('mis-grupos', '/mis-grupos'));
  // btnP1Extranjero queda disabled (v2.1 §2.6) — sin listener, no responde.
};

// ─── 4. Ingreso con código — 2 pasos (v2.1 §2.4, Bloque 6.3) ──────────────
//
// Paso 1: pedir codigo + lookup via getGroupPreview (RPC publica del Bloque 3A).
// Paso 2: mostrar nombre del grupo + pedir apodo del jugador-en-grupo + join.
// El apodo arranca vacio con placeholder "Tu apodo aqui" — no se prefilea con
// global_nickname (que tras Bloque 3C es el email del usuario; el apodo es
// propio del grupo, no del usuario global).
//
// Caso "ya sos miembro": mensaje explicito + boton "Entrar al grupo" en vez
// de navegar silencioso. El cap 5/5 se chequea en Paso 1 (botón deshabilitado);
// si el cap se llena entre paso 1 y paso 2 por race, la RPC unirse_a_grupo
// devuelve `limite_grupos` y se traduce con traducirErrorGrupo.

export const renderIngreso = () => {
  let step = 1;
  let groupPreview = null; // { nombre, codigo, jugadores_count, status }

  const render = () => {
    const reachedLimit = getActiveMembershipsForUser().length >= MAX_GROUPS_PER_USER;

    if (step === 1) {
      document.getElementById('app-root').innerHTML = `
            <div class="glass-card view-enter">
                <h2 class="mb-2" style="font-family: var(--font-heading);">Sumarme a un grupo</h2>
                <p class="subtitle mb-4">Ingresá el código de 6 caracteres del grupo.</p>
                ${reachedLimit ? `<p class="subtitle mb-4" style="font-size:0.78rem;">${MAX_GROUPS_PER_USER}/${MAX_GROUPS_PER_USER} grupos activos</p>` : ''}
                <div class="input-group">
                    <input type="text" id="codigoInput" placeholder="Ej: ABC123" maxlength="6" style="text-transform: uppercase; text-align: center; font-size: 1.5rem; letter-spacing: 5px;">
                </div>
                <div id="errorMsg" class="error-msg"></div>
                <button class="btn btn-primary" id="btnBuscar" ${reachedLimit ? 'disabled style="opacity:.45; cursor:not-allowed;"' : ''}>Buscar grupo</button>
                <button class="btn btn-secondary" onclick="window.location.hash='/'">Volver al inicio</button>
            </div>
        `;

      const btn = document.getElementById('btnBuscar');
      const errorBox = document.getElementById('errorMsg');
      const showError = (t) => { errorBox.textContent = t; errorBox.classList.add('visible'); };

      btn?.addEventListener('click', async () => {
        errorBox.classList.remove('visible');
        const code = document.getElementById('codigoInput').value.toUpperCase().trim();
        if (!code) { showError('Ingresá el código del grupo.'); return; }
        if (code.length !== 6) { showError('El código tiene 6 caracteres.'); return; }

        btn.disabled = true;
        btn.textContent = 'Buscando…';
        const { data, error: err } = await getGroupPreview(code);
        if (err) {
          btn.disabled = false; btn.textContent = 'Buscar grupo';
          showError('No pudimos verificar el grupo. Probá de nuevo.');
          return;
        }
        if (!data) {
          btn.disabled = false; btn.textContent = 'Buscar grupo';
          showError('El grupo no existe.');
          return;
        }
        groupPreview = data;
        step = 2;
        render();
      });
      return;
    }

    // step === 2 — nombre del grupo visible + apodo + Sumarme
    document.getElementById('app-root').innerHTML = `
          <div class="glass-card view-enter">
              <p class="subtitle mb-2" style="font-size:.78rem; letter-spacing:.06em;">SUMATE A</p>
              <h2 class="mb-2" style="font-family: var(--font-heading);">${groupPreview.nombre}</h2>
              <p class="subtitle mb-4">Código: ${groupPreview.codigo}</p>
              <div class="input-group mb-4">
                  <label>Apodo en este grupo (máx 20)</label>
                  <input type="text" id="apodoGrupoIngreso" maxlength="20" placeholder="Tu apodo aquí">
              </div>
              <div id="errorMsg" class="error-msg"></div>
              <button class="btn btn-primary" id="btnSumarme">Sumarme</button>
              <button class="btn btn-secondary" id="btnVolverPaso1">Cambiar de código</button>
          </div>
      `;

    const btn = document.getElementById('btnSumarme');
    const errorBox = document.getElementById('errorMsg');
    const showError = (t) => { errorBox.textContent = t; errorBox.classList.add('visible'); };

    document.getElementById('btnVolverPaso1')?.addEventListener('click', () => {
      step = 1; groupPreview = null; render();
    });

    btn?.addEventListener('click', async () => {
      errorBox.classList.remove('visible');
      const apodo = document.getElementById('apodoGrupoIngreso').value.trim();
      if (!apodo) { showError('Completá tu apodo en el grupo.'); return; }
      if (apodo.length > 20) { showError('El apodo no puede tener más de 20 caracteres.'); return; }
      if (apodo.includes('@')) { showError('El apodo no puede ser un email. Elegí un alias corto.'); return; }

      btn.disabled = true;
      btn.textContent = 'Sumándote…';
      const { data, error: err } = await joinGroup(groupPreview.codigo, apodo);
      if (err) {
        const m = (err.message || '').toLowerCase();
        if (m.includes('ya_sos_miembro')) {
          // Ya pertenece al grupo: ofrecer entrar derecho con el mismo boton.
          setActiveGroupCode(groupPreview.codigo);
          showError('Ya sos parte de este grupo.');
          btn.disabled = false;
          btn.textContent = 'Entrar al grupo';
          btn.onclick = () => navigate(`/g/${groupPreview.codigo}`);
          return;
        }
        btn.disabled = false;
        btn.textContent = 'Sumarme';
        showError(traducirErrorGrupo(err));
        return;
      }
      setActiveGroupCode(data.code);
      navigate(`/g/${data.code}`);
    });
  };

  render();
};

// ─── 5. Creación de grupo ──────────────────────────────────────────────────

export const renderCrearGrupo = () => {
  const reachedLimit = getActiveMembershipsForUser().length >= MAX_GROUPS_PER_USER;
  document.getElementById('app-root').innerHTML = `
        <div class="glass-card view-enter">
            <h2 class="mb-4" style="font-family: var(--font-heading);">Crear grupo</h2>
            ${reachedLimit ? `<p class="subtitle mb-4" style="font-size:0.78rem;">${MAX_GROUPS_PER_USER}/${MAX_GROUPS_PER_USER} grupos activos</p>` : ''}
            <div class="input-group">
                <label>Nombre del grupo</label>
                <input type="text" id="grupoNombre" placeholder="Ej: Los Pibes de Sistemas" maxlength="40">
            </div>
            <div class="input-group mb-6">
                <label>¿Cómo te conocen en este grupo? (máx 20)</label>
                <input type="text" id="apodoGrupo" placeholder="Ej: Nico" maxlength="20">
            </div>
            <div id="errorMsg" class="error-msg"></div>
            <button class="btn btn-primary" id="btnCrear" ${reachedLimit ? 'disabled style="opacity:.45; cursor:not-allowed;"' : ''}>Crear grupo</button>
            <button class="btn btn-secondary" onclick="window.location.hash='/'">Cancelar</button>
        </div>
    `;

  const btn = document.getElementById('btnCrear');
  const error = document.getElementById('errorMsg');
  const showError = (text) => { error.textContent = text; error.classList.add('visible'); };

  btn.addEventListener('click', async () => {
    error.classList.remove('visible');
    const nombre = document.getElementById('grupoNombre').value.trim();
    const apodo = document.getElementById('apodoGrupo').value.trim();
    if (!nombre || !apodo) { showError('Completá el nombre del grupo y cómo te conocen.'); return; }
    if (apodo.length > 20) { showError('El apodo no puede tener más de 20 caracteres.'); return; }
    if (apodo.includes('@')) { showError('El apodo no puede ser un email. Elegí un alias corto.'); return; }

    btn.disabled = true;
    btn.textContent = 'Creando…';
    const { data, error: err } = await createGroup(nombre, apodo);
    if (err) {
      btn.disabled = false;
      btn.textContent = 'Crear grupo';
      showError(traducirErrorGrupo(err));
      return;
    }
    setActiveGroupCode(data.code);
    navigate(`/g/${data.code}`);
  });
};

// ─── 6. Mis Grupos Activos — pantalla dedicada (v2.1 §2.5, Bloque 7) ──────
//
// Destino del intent 'mis-grupos' (Pantalla 1) y de la ruta /mis-grupos.
// Cierra DT-DH (botón "Mis grupos activos" de P1 ahora cambia el hash a
// /mis-grupos => hashchange dispara aunque antes estuvieras en '/').
//
// Caso vacío (sin grupos): mensaje + CTAs Crear/Unirme + Cerrar sesión sutil
// (decision D del Bloque 7 = Opcion A: cerrar sesion solo aparece aca, no
// cuando hay grupos, porque ese caso entra a un grupo y cierra desde Perfil
// segun v2.1 §4.2). Cierra DT-DI.
//
// Caso con grupos: lista de cards .mis-grupos-card (estetica copiada del
// modal openGroupSelector pero como pantalla, no modal). Tap -> switchActiveGroup.

export const renderMisGrupos = () => {
  const grupos = getKnownGroups();
  const reachedLimit = grupos.length >= MAX_GROUPS_PER_USER;

  if (grupos.length === 0) {
    // Auditoría boot-hang: si la última carga de grupos falló (timeout/red), el
    // cache está vacío pero NO porque el usuario no tenga grupos. Avisar + ofrecer
    // recargar en vez de mostrar "no tenés grupos" (engañoso) y empujar a crear.
    const cargaFallo = gruposLoadFailed();
    const avisoFallo = cargaFallo
      ? `<div class="glass-card" style="margin-bottom:1rem;border-color:rgba(248,113,113,.28);background:rgba(248,113,113,.07);text-align:center;">
                    <p class="subtitle" style="color:#fecaca;margin-bottom:.7rem;">No pudimos cargar tus grupos (conexión). Probá de nuevo.</p>
                    <button class="btn btn-secondary" type="button" style="min-height:42px;" onclick="location.reload()">Reintentar</button>
                 </div>`
      : '';
    document.getElementById('app-root').innerHTML = `
            <div class="view-enter" style="text-align:center; margin-top:2rem;">
                ${renderBrandLogo()}
                ${avisoFallo}
                <p class="subtitle mb-6">${cargaFallo ? 'Si ya tenés grupos, reintentá arriba.' : 'No tenés grupos activos todavía.'}</p>

                <button class="btn btn-primary"   id="btnMGCrear">Crear grupo</button>
                <button class="btn btn-secondary" id="btnMGUnirse">Sumarme a un grupo</button>
                <button class="btn btn-secondary" id="btnMGCerrarSesion" style="font-size:0.8rem; opacity:0.72;">Cerrar sesión</button>
            </div>
        `;
    document.getElementById('btnMGCrear') ?.addEventListener('click', () => { window.location.hash = '/crear-grupo'; });
    document.getElementById('btnMGUnirse')?.addEventListener('click', () => { window.location.hash = '/ingreso';     });
    document.getElementById('btnMGCerrarSesion')?.addEventListener('click', async () => {
      // Full reload (audit 5-jun): sin recarga, SIGNED_OUT re-renderiza por SPA y
      // el cliente Supabase persistía zombie → colgaba el próximo login.
      await signOut();
      window.location.href = '/?_=' + Date.now();
    });
    return;
  }

  const cards = grupos.map(({ codigo, grupo }) => `
              <button class="mis-grupos-card" type="button" data-codigo="${codigo}">
                  <strong>${getGroupDisplayName(grupo)}</strong>
                  <em><span>Código:</span> ${codigo}</em>
                  <small>${grupo.jugadores?.length || 0} ${(grupo.jugadores?.length || 0) === 1 ? 'jugador' : 'jugadores'}</small>
              </button>
      `).join('');

  document.getElementById('app-root').innerHTML = `
          <div class="glass-card view-enter">
              <h2 class="mb-2" style="font-family: var(--font-heading); text-align:center;">Mis Grupos Activos</h2>
              <p class="subtitle mb-4" style="text-align:right; font-size:0.72rem;">${grupos.length}/${MAX_GROUPS_PER_USER} grupos activos</p>
              <div id="misGruposList">${cards}</div>
              <button class="btn btn-secondary" id="btnMGCrear2"  ${reachedLimit ? 'disabled style="opacity:.45; cursor:not-allowed;"' : ''}>Crear grupo</button>
              <button class="btn btn-secondary" id="btnMGUnirse2" ${reachedLimit ? 'disabled style="opacity:.45; cursor:not-allowed;"' : ''}>Sumarme a un grupo</button>
          </div>
      `;

  // Tap en card -> switchActiveGroup ya hace setActiveGroupCode + navigate.
  document.querySelectorAll('.mis-grupos-card').forEach((btn) => {
    btn.addEventListener('click', () => switchActiveGroup(btn.dataset.codigo));
  });
  document.getElementById('btnMGCrear2') ?.addEventListener('click', () => { if (!reachedLimit) window.location.hash = '/crear-grupo'; });
  document.getElementById('btnMGUnirse2')?.addEventListener('click', () => { if (!reachedLimit) window.location.hash = '/ingreso';     });
};

// ─── Bridges window.* para consumo desde router.js ─────────────────────────
window.renderLanding = renderLanding;
window.renderIngreso = renderIngreso;
window.renderCrearGrupo = renderCrearGrupo;
window.renderMisGrupos = renderMisGrupos;

// ─── Bootstrap con gate de auth + cache de grupos (F1.2 / F1.3.b) ──────────
// La app espera el estado de auth de Supabase y la hidratación del cache de
// grupos (cargarMisGrupos) antes de pintar nada con sesión.

let lastAuthState = null;

// Bloque 6.5: modo recovery (PASSWORD_RECOVERY). Flag modulo-level que el
// bootstrap setea de dos formas:
//   1) Pre-init: detectando `type=recovery` en el hash inicial ANTES de
//      initAuth — detectSessionInUrl puede consumir el hash y emitir el
//      evento PASSWORD_RECOVERY antes de que registremos el callback de
//      onAuthStateChange, en cuyo caso se perderia. Leer la URL primero
//      cubre la carrera.
//   2) Runtime: cuando llega event === 'PASSWORD_RECOVERY' en el listener.
// Mientras recoveryMode=true, se ignoran SIGNED_IN/INITIAL_SESSION/
// USER_UPDATED para que el usuario complete el reset sin saltos visuales.
// exitRecoveryMode() lo limpia y dispara el flow normal.
let recoveryMode = false;

const isRecoveryUrl = () => (window.location.hash || '').includes('type=recovery');

const exitRecoveryMode = () => {
  recoveryMode = false;
  // Limpiar residuos del hash (Supabase ya lo limpia, defensivo) y forzar
  // un render del flow normal.
  window.location.hash = '/';
  renderApp();
};

// Whitelist de rutas accesibles para visitante anon → src/core/rutas-publicas.js
// (extraída para testear el gate real sin bootstrapear main.js). Incluye la
// Tribuna Pública entera + sus sub-vistas (cábalas/promesas) + link viral /t/.

const renderGrupoInexistente = (codigo) => {
  document.getElementById('app-root').innerHTML = `
        <div class="glass-card view-enter" style="text-align:center;">
            ${renderBrandLogo()}
            <p class="subtitle mb-6">El grupo no existe.<br><span style="font-size:.78rem; opacity:.7;">Código: ${codigo}</span></p>
            <button class="btn btn-primary" id="btnGINoVolver">Ir al inicio</button>
        </div>
    `;
  document.getElementById('btnGINoVolver')?.addEventListener('click', () => { window.location.hash = '/'; });
};

// Loader pintado mientras el join esta en progreso (Bloque 8 pulir flash).
// `preview` puede ser null si todavia no recuperamos el nombre del grupo.
const renderSumandote = (preview) => {
  const nombre = preview?.nombre ? preview.nombre : 'tu grupo';
  document.getElementById('app-root').innerHTML = `
        <div class="glass-card view-enter" style="text-align:center;">
            <p class="subtitle mb-2" style="font-size:.78rem; letter-spacing:.06em;">SUMÁNDOTE A</p>
            <h2 class="mb-4" style="font-family: var(--font-heading);">${nombre}</h2>
            <div class="pacto-spinner" style="margin: 1.5rem auto;"></div>
            <p class="subtitle" style="font-size:.9rem;">Conectándote al grupo…</p>
        </div>
    `;
};

// joinGroup envuelto: setActiveGroupCode + navigate al home. Inyectado a
// renderInvitado para no acoplarlo al router. Cuando el join sale OK,
// router pinta renderGrupo (situacion miembro).
const onJoinFromInvitado = async (codigo, apodo) => {
  const { data, error } = await joinGroup(codigo, apodo);
  // Bloque 8 (pulir flash): siempre limpiar el flag (caso exito y error).
  clearJoinInProgress();
  if (error) {
    // Fix cap-grupos (post-smoke Bloque 8): el alert() era unreliable en
    // mobile (popup-blocker, focus). Propagar el mensaje a renderInvitado
    // como banner persistente arriba — handleGuestRoute lo pasa como
    // initialErrorMsg. Cubre todos los errores de joinGroup
    // (limite_grupos, apodo_ocupado, grupo_cerrado, codigo_invalido).
    const msg = traducirErrorGrupo(error);
    handleGuestRoute(codigo, true, msg);
    return { error };
  }
  setActiveGroupCode(data.code);
  navigate(`/g/${data.code}`);
  // Fix bug Bloque 8: cuando entramos por link de invitado, hash ya es
  // /g/CODE; navigate(mismo) NO dispara hashchange y la pantalla del
  // invitado queda visible aunque el join paso. router() lee el hash y
  // pinta renderGrupo (cache tiene el grupo nuevo porque joinGroup
  // hidrato cargarMisGrupos al final). Idempotente si navigate si
  // disparo hashchange.
  router();
  return { data };
};

// Bloque 8 (v2.1 §3): carril del invitado por link /g/CODIGO.
// Intercept antes del router para cubrir las 3 situaciones del §3.2:
//   A) Con sesion + miembro     -> setActive + router (renderGrupo).
//   B) Con sesion + NO miembro  -> renderInvitado modo logueado (solo apodo).
//   C) Sin sesion               -> renderInvitado modo invitado (auth+apodo).
//
// Si getGroupPreview devuelve null -> renderGrupoInexistente.
//
// Post-OAuth del flujo C con Google: tras volver al dominio, hay sesion +
// no es miembro + sessionStorage tiene { intent:'invitado', codigo, apodo }
// matching el codigo actual. Auto-disparamos joinGroup y la pantalla del
// invitado nunca llega a pintarse — el usuario aterriza directo en el home.
const handleGuestRoute = async (codigo, authed, initialErrorMsg = null) => {
  // Situacion A.
  if (authed) {
    const user = getCurrentUser();
    if (user && esUsuarioMiembroDelGrupo(user.user_id, codigo)) {
      setActiveGroupCode(codigo);
      router();
      return;
    }
  }

  // Bloque 8 (pulir flash): si hay un join en progreso para este codigo,
  // pintar loader "Sumandote..." en vez del generico para evitar el flash
  // de renderInvitado modo logueado entre SIGNED_IN y joinGroup OK.
  const joining = authed && isJoiningCode(codigo);
  if (joining) {
    renderSumandote(null);
  } else {
    // Render intermedio mientras esperamos getGroupPreview (RPC, ~100-300ms).
    document.getElementById('app-root').innerHTML = `
        <div class="glass-card view-enter" style="text-align:center;">
            <p class="subtitle">Cargando grupo...</p>
        </div>
    `;
  }

  const { data: preview, error: previewErr } = await getGroupPreview(codigo);
  if (previewErr || !preview) {
    renderGrupoInexistente(codigo);
    return;
  }

  // Auto-join post-OAuth (intent 'invitado' guardado pre-redirect).
  if (authed) {
    try {
      const raw = sessionStorage.getItem(LOGIN_INTENT_KEY);
      if (raw) {
        const stored = JSON.parse(raw);
        if (stored?.intent === 'invitado' && stored?.codigo === codigo && stored?.apodo) {
          sessionStorage.removeItem(LOGIN_INTENT_KEY);
          // Setear flag para coherencia con el caso email+clave (loader
          // visible durante el auto-join).
          try { sessionStorage.setItem(JOIN_IN_PROGRESS_KEY, codigo); } catch (_) { /* ignore */ }
          renderSumandote(preview);
          await onJoinFromInvitado(codigo, stored.apodo);
          // onJoinFromInvitado limpia el flag y, si error, re-pinta + alert.
          return;
        }
      }
    } catch (_) { /* sessionStorage roto -> sigue al render normal */ }
  }

  // Bloque 8 (pulir flash): si seguimos en modo joining (viene de email+clave
  // o modo logueado situacion B), mantener loader con nombre del grupo y
  // esperar a que onJoinFromInvitado disparado por el handler de login.js
  // repinte cuando termine.
  if (joining) {
    renderSumandote(preview);
    return;
  }

  renderInvitado(codigo, preview, onJoinFromInvitado, initialErrorMsg);
};

// Fix vuelta-de-MP (v3.43): MercadoPago devuelve a la RAÍZ (sin hash) tras pagar.
// El boot rutea solo por `grupo_activo`/cache y NUNCA lee el flag de cobro
// pendiente → el pagador caía en el home/ingreso en vez del pacto, y el
// overlay+poll (que vive en la vista del pacto) no disparaba. Este hook lee el
// flag y, si el grupo ya está en cache, rutea a /pacto/CODE para que el overlay
// dispare en contexto. One-shot por carga (no rebota en loop si nunca certifica;
// el flag además tiene TTL de 10 min, que lo limpia pacto.js).
let certBootHandled = false;
// Lee el retorno de MercadoPago del query string (MP appendea ?status=&payment_id=
// &collection_status=... a la raíz). Devuelve 'pagado' si hay un pago real en curso
// (status approved/pending/in_process/authorized o payment_id real), 'cancelado' si
// volvió SIN pagar (status null/rejected/cancelled/failure y sin payment_id real),
// 'ninguno' si no hay params de MP. Inline (idéntica en pacto.js) para no sumar un
// archivo/import nuevo al flujo de pago a días del lanzamiento.
const leerRetornoMP = () => {
  let sp;
  try { sp = new URLSearchParams(window.location.search || ''); }
  catch { return 'ninguno'; }
  const CLAVES = ['status', 'collection_status', 'payment_id', 'collection_id', 'preference_id'];
  if (!CLAVES.some((k) => sp.has(k))) return 'ninguno';
  const status = (sp.get('status') || sp.get('collection_status') || '').toLowerCase();
  const paymentId = sp.get('payment_id') || sp.get('collection_id') || '';
  const real = (v) => Boolean(v) && v !== 'null';
  const PAGANDO = ['approved', 'pending', 'in_process', 'authorized'];
  const CANCELADO = ['rejected', 'cancelled', 'refunded', 'charged_back', 'failure', 'null'];
  if (PAGANDO.includes(status)) return 'pagado';
  if (CANCELADO.includes(status)) return 'cancelado';
  return real(paymentId) ? 'pagado' : 'cancelado';
};
const rutearPagoPendiente = () => {
  let info;
  try { info = JSON.parse(localStorage.getItem('cert_pago_pendiente') || 'null'); }
  catch { return false; }
  if (!info?.codigo || !info?.group_id) return false;
  if (info.ts && Date.now() - info.ts > 600000) return false; // >10 min: lo descarta pacto.js
  // Solo se rutea al pacto (→ overlay "Procesando…") con SEÑAL POSITIVA de pago.
  // CUALQUIER otra cosa — status=null/cancelado/rechazado, O bfcache que restaura
  // la página SIN params (botón Atrás) — limpia el flag y NO rutea. Sin esto, el
  // overlay colgaba la vista 60s aunque nunca hubo pago (fix v3.43-bis). Trade-off
  // aceptado: recargar manual con pago aún pendiente pierde el overlay, pero si
  // certifica se ve certificado igual al recargar — a cambio mata el cuelgue en
  // TODOS los caminos de Atrás/cancelar.
  if (leerRetornoMP() !== 'pagado') {
    localStorage.removeItem('cert_pago_pendiente');
    return false;
  }
  // El grupo tiene que estar en cache para que renderPacto no rebote a /ingreso.
  const user = getCurrentUser();
  if (!user || !esUsuarioMiembroDelGrupo(user.user_id, info.codigo)) return false;
  certBootHandled = true;
  setActiveGroupCode(info.codigo);
  navigate(`/pacto/${info.codigo}`);
  return true;
};

const renderApp = () => {
  const authed = Boolean(getCurrentUser());
  lastAuthState = authed;
  const hash = (window.location.hash || '').slice(1);

  // Vuelta de MP a la raíz con cobro pendiente → ruteo al pacto del grupo.
  // Antes del ruteo normal; corre después de cargarMisGrupos (cache listo).
  if (authed && !certBootHandled && (hash === '' || hash === '/')) {
    if (rutearPagoPendiente()) return;
  }

  // Bloque 7 (v2.1 §2.5): Mis Grupos como pantalla dedicada via /mis-grupos.
  // Con sesion -> renderMisGrupos. Sin sesion -> renderLogin('mis-grupos')
  // (applyIntent navega post-login al destino correcto).
  // Se chequea antes que cualquier otra rama para que valga incluso si el
  // user esta logueado (no queremos que pase al router y caiga a auto-grupo).
  if (hash === '/mis-grupos') {
    if (authed) { renderMisGrupos(); return; }
    renderLogin('mis-grupos');
    return;
  }

  // Bloque 8 (v2.1 §3): carril del invitado /g/CODIGO. Intercept antes del
  // router para situaciones A/B/C del §3.2. Reemplaza el gate transitorio
  // del Bloque 5 que mandaba a renderLogin sin codigo.
  if (hash.startsWith('/g/')) {
    const codigo = hash.split('/')[2]?.toUpperCase();
    if (!codigo) { renderLanding(); return; }
    handleGuestRoute(codigo, authed);
    return;
  }

  if (authed) { router(); return; }
  if (esRutaPublica(hash)) { router(); return; }
  // Bloque 5 (v2.1 §2.1): sin sesión, la gate universal a renderLogin deja
  // de existir. Los botones de Pantalla 1 (renderLanding) son los que llaman
  // a renderLogin(intent) cuando hace falta.
  renderLanding();
};

const bootstrap = async () => {
  scheduleBootWatchdog();
  // Bloque 6.5: detectar recovery ANTES de initAuth (que dispara
  // detectSessionInUrl + el evento PASSWORD_RECOVERY). Si no se chequea
  // aca, el evento se puede perder porque el listener se registra dentro
  // de initAuth y la emision puede ocurrir antes.
  recoveryMode = isRecoveryUrl();
  try {
    await initAuth();
    if (getCurrentUser()) await cargarMisGrupos();
  } catch (err) {
    console.error('[main] bootstrap falló:', err);
  }
  onAuthStateChange(async (event) => {
    // Bloque 6.5: PASSWORD_RECOVERY tiene prioridad — pinta pantalla de reset.
    if (event === 'PASSWORD_RECOVERY') {
      recoveryMode = true;
      renderResetPassword(exitRecoveryMode);
      return;
    }
    // Mientras estemos en modo recovery, ignorar otros eventos
    // (SIGNED_IN / INITIAL_SESSION / USER_UPDATED post-updateUser) para no
    // interrumpir la pantalla de reset.
    if (recoveryMode) return;
    const authed = Boolean(getCurrentUser());
    if (authed === lastAuthState) return;
    if (authed) {
      try { await cargarMisGrupos(); }
      catch (err) { console.error('[main] cargarMisGrupos tras login falló:', err); }
    }
    renderApp();
  });
  // First render: si entramos por recovery link, pintar pantalla de reset;
  // si no, flow normal.
  if (recoveryMode) {
    renderResetPassword(exitRecoveryMode);
  } else {
    renderApp();
  }
  // Primer render listo → bajar el overlay de carga (foto) por opacity.
  hideLoadingOverlay();
};

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  window.addEventListener('hashchange', renderApp);

  // Fix freeze-mobile (6-jun): al restaurar la página desde bfcache (resume tras
  // suspender el celu / back-forward), las conexiones quedan muertas pero el JS
  // sigue con estado viejo. e.persisted=true marca ese caso → recargamos para
  // arrancar con sesión/conexión frescas. NO dispara en cargas normales.
  window.addEventListener('pageshow', (e) => { if (e.persisted) window.location.reload(); });

  if (document.readyState === 'complete') {
    bootstrap();
  } else {
    document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
  }

  // ─── H5: Vercel Analytics (tráfico + Web Vitals) ──────────────────────────
  // Solo en build de producción. El toggle del panel de Vercel lo activa el
  // dueño; inject() agrega el script de medición. try/catch defensivo.
  if (import.meta.env.PROD) {
    import('@vercel/analytics')
      .then(({ inject }) => inject())
      .catch((err) => console.error('[analytics] inject falló:', err));
  }

  // ─── H4: Service Worker (warm starts + updates automáticos + kill switch) ──
  // Solo en build de producción: en `vite dev` el SW interfiere con el HMR.
  // import.meta.env.PROD lo resuelve Vite a true solo en `vite build`/preview.
  // Registrado como módulo para poder testear las estrategias desde vitest.
  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js', { type: 'module' }).then(
        (reg) => {
          reg.addEventListener('updatefound', () => {
            console.log('[sw] update encontrado, instalando en background');
          });
        },
        (err) => { console.error('[sw] registro falló:', err); },
      );
    });
  }
}
